import { useEffect, useRef } from 'react';
import { EditorState } from '@codemirror/state';
import { EditorView, keymap, lineNumbers, highlightActiveLineGutter, highlightSpecialChars, drawSelection, highlightActiveLine } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { syntaxHighlighting, defaultHighlightStyle, bracketMatching, foldGutter, indentOnInput } from '@codemirror/language';
import { yaml } from '@codemirror/lang-yaml';
import { oneDark } from '@codemirror/theme-one-dark';
import { searchKeymap, highlightSelectionMatches } from '@codemirror/search';

// Custom dark theme to match the app
const customTheme = EditorView.theme({
  '&': {
    backgroundColor: '#1a1a1c',
    color: '#ececf1',
    height: '100%',
  },
  '.cm-content': {
    caretColor: '#ececf1',
    fontFamily: 'JetBrains Mono, monospace',
  },
  '.cm-cursor, .cm-dropCursor': {
    borderLeftColor: '#ececf1',
  },
  '&.cm-focused .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection': {
    backgroundColor: '#6366f133',
  },
  '.cm-gutters': {
    backgroundColor: '#1a1a1c',
    color: '#565869',
    border: 'none',
  },
  '.cm-activeLineGutter': {
    backgroundColor: '#202123',
    color: '#acacbe',
  },
  '.cm-activeLine': {
    backgroundColor: '#202123',
  },
  '.cm-foldGutter': {
    color: '#565869',
  },
  '.cm-scroller': {
    overflow: 'auto',
  },
}, { dark: true });

interface CodeEditorProps {
  value: string;
  onChange?: (value: string) => void;
  readOnly?: boolean;
  fontSize?: number;
}

export default function CodeEditor({ value, onChange, readOnly = false, fontSize = 12 }: CodeEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const onChangeRef = useRef(onChange);
  // Track the last value we sent via onChange to avoid reacting to our own changes
  const lastEmittedValueRef = useRef(value);

  // Keep onChange ref in sync
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Initialize editor
  useEffect(() => {
    if (!containerRef.current) return;

    const updateListener = EditorView.updateListener.of((update) => {
      if (update.docChanged && onChangeRef.current) {
        const newValue = update.state.doc.toString();
        lastEmittedValueRef.current = newValue;
        onChangeRef.current(newValue);
      }
    });

    const state = EditorState.create({
      doc: value,
      extensions: [
        lineNumbers(),
        highlightActiveLineGutter(),
        highlightSpecialChars(),
        history(),
        foldGutter(),
        drawSelection(),
        EditorState.allowMultipleSelections.of(true),
        indentOnInput(),
        bracketMatching(),
        highlightActiveLine(),
        highlightSelectionMatches(),
        keymap.of([
          ...defaultKeymap,
          ...searchKeymap,
          ...historyKeymap,
          indentWithTab,
        ]),
        yaml(),
        syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
        oneDark,
        customTheme,
        EditorView.lineWrapping,
        EditorState.readOnly.of(readOnly),
        EditorView.theme({
          '.cm-content': {
            fontSize: `${fontSize}px`,
          },
          '.cm-gutters': {
            fontSize: `${fontSize}px`,
          },
        }),
        updateListener,
      ],
    });

    const view = new EditorView({
      state,
      parent: containerRef.current,
    });

    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
  // Only recreate editor when readOnly or fontSize changes
  }, [readOnly, fontSize]);

  // Sync external value changes (skip if it's our own change echoing back)
  useEffect(() => {
    // Skip if this value matches what we last emitted (it's our own change coming back)
    if (value === lastEmittedValueRef.current) {
      return;
    }

    const view = viewRef.current;
    if (!view) return;

    const currentValue = view.state.doc.toString();
    if (currentValue !== value) {
      // Update the ref so we don't react to this change again
      lastEmittedValueRef.current = value;
      view.dispatch({
        changes: {
          from: 0,
          to: currentValue.length,
          insert: value,
        },
      });
    }
  }, [value]);

  return (
    <div
      ref={containerRef}
      className="h-full w-full overflow-hidden"
    />
  );
}
